import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  selectConversationsQuota,
  selectMessages,
  selectOneChatComposed,
  selectOneChatSettings,
} from '@/features';
import { useAppSelector } from '@/store';

type OneChatIntroStore = {
  customMessage: string;
  isSequenceFulfilled: boolean;
};

type OneChatIntroListener = (state: OneChatIntroStore) => void;

const listeners = new Set<OneChatIntroListener>();

let oneChatIntroStore: OneChatIntroStore = {
  customMessage: '',
  isSequenceFulfilled: true,
};

const subscribe = (listener: OneChatIntroListener) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const setOneChatIntroStore = (nextState: Partial<OneChatIntroStore>) => {
  oneChatIntroStore = {
    ...oneChatIntroStore,
    ...nextState,
  };

  listeners.forEach((listener) => listener(oneChatIntroStore));
};

export const useOneChatIntroData = () => {
  const [{ customMessage, isSequenceFulfilled }, setOneChatIntroState] =
    useState(oneChatIntroStore);

  useEffect(() => {
    const syncStore = (nextState: OneChatIntroStore) => {
      setOneChatIntroState(nextState);
    };

    syncStore(oneChatIntroStore);

    return subscribe(syncStore);
  }, []);

  const settingsLoaded = useAppSelector(selectOneChatSettings.isLoaded);
  const messagesLoaded = useAppSelector(selectMessages.isLoaded);
  const quotaLoaded = useAppSelector(selectConversationsQuota.isLoaded);
  const aiIntroMessage = useAppSelector(selectOneChatComposed.aiIntroMessage);
  const manualIntroMessage = useAppSelector(
    selectOneChatComposed.manualIntroMessage,
  );
  const aiLegalMessage = useAppSelector(selectOneChatComposed.aiLegalMessage);
  const offlineMessage = useAppSelector(
    selectOneChatComposed.offlineIntroMessage,
  );
  const noIntroInHistory = useAppSelector(selectMessages.shouldSendIntro);
  const noLegalInHistory = useAppSelector(selectMessages.shouldSendLegal);
  const noOfflineMessageInHistory = useAppSelector(
    selectMessages.shouldSendOfflineMessage,
  );
  const shouldSendSuggestions = useAppSelector(
    selectOneChatComposed.shouldSendSuggestions,
  );
  const shouldShowContactFormInIntroSequence = useAppSelector(
    selectOneChatComposed.shouldShowContactFormInIntroSequence,
  );
  const contactFormIntroMessage = useAppSelector(
    selectOneChatSettings.contactFormIntroMessage,
  );
  const aiSuggestedQuestions = useAppSelector(
    selectOneChatComposed.aiSuggestions,
  );
  const aiSuggestionsIntroMessage = useAppSelector(
    selectOneChatComposed.aiSuggestionsIntroMessage,
  );

  const loaded = useMemo(
    () => settingsLoaded && messagesLoaded && quotaLoaded,
    [settingsLoaded, messagesLoaded, quotaLoaded],
  );

  const shouldSendAiIntro = useMemo(
    () => Boolean(noIntroInHistory && aiIntroMessage),
    [noIntroInHistory, aiIntroMessage],
  );

  const shouldSendManualIntro = useMemo(
    () => Boolean(noIntroInHistory && manualIntroMessage),
    [noIntroInHistory, manualIntroMessage],
  );

  const shouldSendAiLegal = useMemo(
    () => Boolean(noLegalInHistory && aiLegalMessage),
    [noLegalInHistory, aiLegalMessage],
  );

  const shouldSendOfflineMessage = useMemo(
    () => Boolean(noOfflineMessageInHistory && offlineMessage),
    [noOfflineMessageInHistory, offlineMessage],
  );

  const shouldSendCustomMessage = useMemo(
    () => Boolean(customMessage),
    [customMessage],
  );

  const addCustomMessage = useCallback((message: string) => {
    setOneChatIntroStore({ customMessage: message });
  }, []);

  const setIsSequenceFulfilled = useCallback((nextValue: boolean) => {
    setOneChatIntroStore({ isSequenceFulfilled: nextValue });
  }, []);

  return {
    readiness: {
      loaded,
      hasAnyIntro:
        loaded &&
        (shouldSendAiIntro ||
          shouldSendManualIntro ||
          shouldSendAiLegal ||
          shouldSendSuggestions ||
          shouldSendOfflineMessage ||
          shouldSendCustomMessage ||
          shouldShowContactFormInIntroSequence),
    },
    intro: {
      aiMessage: aiIntroMessage,
      manualMessage: manualIntroMessage,
      shouldSendAiIntro,
      shouldSendManualIntro,
    },
    legal: {
      message: aiLegalMessage,
      shouldSend: shouldSendAiLegal,
    },
    suggestions: {
      questions: aiSuggestedQuestions,
      introMessage: aiSuggestionsIntroMessage,
      shouldSend: shouldSendSuggestions,
    },
    offline: {
      message: offlineMessage,
      shouldSend: shouldSendOfflineMessage,
    },
    contactForm: {
      introMessage: contactFormIntroMessage,
      shouldShowInIntroSequence: shouldShowContactFormInIntroSequence,
    },
    custom: {
      message: customMessage,
      shouldSend: shouldSendCustomMessage,
    },
    isSequenceFulfilled,
    setIsSequenceFulfilled,
    addCustomMessage,
  };
};
